import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchPostageAccountDetails, changePostageAccountOwner, addAuthorizedUserToPostageAccount, removeAuthorizedUserFromPostageAccount, fetchUserDetailsByNetId, createPostageUser } from '../services/api';
import { useUser } from '../context/UserContext';

import Collapse from 'react-bootstrap/Collapse';
import Button from 'react-bootstrap/Button';
import InputGroup from 'react-bootstrap/InputGroup';

function PostageAccountDetails() {
    const { postageAccountNumber } = useParams();
    const [account, setAccount] = useState(null);
    const [newOwnerNetId, setNewOwnerNetId] = useState('');
    const [newAuthUserNetId, setNewAuthUserNetId] = useState('');
    const [remAuthUserNetId, setRemAuthUserNetId] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const navigate = useNavigate();

    const { user } = useUser();

    const [ownerTransferOpen, setOwnerTransferOpen] = useState(false);
    const [addUserOpen, setAddUserOpen] = useState(false);
    const [remUserOpen, setRemUserOpen] = useState(false);

    useEffect(() => {
        loadAccountDetails();
    }, [postageAccountNumber]);

    const loadAccountDetails = async () => {
        try {
            const details = await fetchPostageAccountDetails(Number(postageAccountNumber));
            setAccount(details);
            setError('');
        } catch (error) {
            console.error('Failed to fetch account details:', error);
            setError('Failed to fetch account details');
        }
    };

    const handleAddAuthorizedUserSelf = async () => {
        try {
            await addAuthorizedUserToPostageAccount(postageAccountNumber, user.netId);
            setSuccess('Authorized user successfully added.');
            setNewAuthUserNetId('');
            loadAccountDetails();
            setTimeout(() => {
                setSuccess('');
            }, 5000);
        } catch (error) {
            console.error('Failed to add authorized user: ', error);
            setError('Failed to add authorized user.');
            setTimeout(() => {
                setError('');
            }, 5000);
        }
    }

    const handleRemoveAuthorizedUserSelf = async () => {
        try {
            await removeAuthorizedUserFromPostageAccount(postageAccountNumber, user.netId);
            setSuccess('Authorized user successfully removed.');
            setNewAuthUserNetId('');
            loadAccountDetails();
            setTimeout(() => {
                setSuccess('');
            }, 5000);
        } catch (error) {
            console.error('Failed to remove authorized user: ', error);
            setError('Failed to remove authorized user.');
            setTimeout(() => {
                setError('');
            }, 10000);
        }
    }

    const handleAddAuthorizedUser = async () => {
        try {
            // First, try to fetch the user to see if they already exist
            try {
                await fetchUserDetailsByNetId(newAuthUserNetId);
            } catch (fetchError) {
                // User does not exist, create them
                const newUserDetails = {
                    netId: newAuthUserNetId,
                    firstName: '',
                    lastName: '',
                    department: '',
                    boxNumber: '',
                    phone: ''
                };
                await createPostageUser(newUserDetails);
            }
            // Whether existing or new, add as authorized user
            await addAuthorizedUserToPostageAccount(postageAccountNumber, newAuthUserNetId);
            setSuccess('Authorized user added successfully.');
            setNewAuthUserNetId('');
            loadAccountDetails();
            setTimeout(() => {
                setSuccess('');
            }, 6000);
        } catch (error) {
            console.error('Failed to add authorized user: ', error);
            setError('Failed to add authorized user. Please ensure the user\'s NetID is valid.');
            setTimeout(() => {
                setError('');
            }, 6000);
        }
    };


    const handleRemoveAuthorizedUser = async () => {
        try {
            await removeAuthorizedUserFromPostageAccount(postageAccountNumber, remAuthUserNetId);
            setSuccess('Authorized user removed successfully.');
            setRemAuthUserNetId('');
            loadAccountDetails();
            setTimeout(() => {
                setSuccess('');
            }, 6000);
        } catch (error) {
            console.error('Failed to remove authorized user: ', error);
            setError('Failed to remove authorized user. Please ensure the user\'s NetId is valid and they are on the Postage Account\'s authorized user list.');
            setTimeout(() => {
                setError('');
            }, 6000);
        }
    }

    const handleEdit = () => {
        navigate(`/postage-account/${postageAccountNumber}/edit`);
    };

    const handleWorktagRequest = () => {
        navigate(`/postage-account/${postageAccountNumber}/WorktagChangeRequest`);
    }

    const handleOwnershipChange = async () => {
        try {
            await changePostageAccountOwner(postageAccountNumber, newOwnerNetId);
            setSuccess('Ownership transferred successfully.');
            setOwnerTransferOpen(false);
            loadAccountDetails();

            setTimeout(() => {
                setSuccess('');
            }, 6000);

        } catch (error) {
            console.error('Failed to transfer ownership:', error);
            setError('Failed to transfer ownership. Please ensure the new owner\'s NetID is valid and that they have a PAN User Account.');
            setSuccess('');

            setTimeout(() => {
                setError('');
            }, 6000);
        }
    };

    // Show transfer options if user is admin or owner of the account
    const isOwner = user && (user.accountLevel == 2 || user.netId == account?.owner?.netId);
    const isAuthorized = user && (account?.authorizedUsers?.some(au => au.netId == user.netId));

    const handleToggleAddUser = () => {
        // If the 'Add User' section is closed, it will open and close the 'Remove User' section if open
        setAddUserOpen(!addUserOpen);
        if (remUserOpen) {
            setRemUserOpen(false);
        }
    };

    const handleToggleRemoveUser = () => {
        // If the 'Remove User' section is closed, it will open and close the 'Add User' section if open
        setRemUserOpen(!remUserOpen);
        if (addUserOpen) {
            setAddUserOpen(false);
        }
    };

    return (
        <div className="container mt-3">
            <div className="row">
                <div className="col-12">
                    {error && <div className="alert alert-danger">{error}</div>}
                    {success && <div className="alert alert-success">{success}</div>}
                    <div id="info-primary">
                        <div className="page-header"><h1 className="gradient-under">Postage Account {postageAccountNumber}</h1></div>
                        <p className="h5"><b>Department:</b> {account?.department}</p>
                        <p className="h5"><b>Worktag String:</b> {account?.worktagString}</p>
                    </div>

                    <h2 className="mt-4">Comments</h2>
                    <div className="col-12 my-3 p-3 border rounded">
                        <div id="pa-comments">
                            {account?.comments || 'None'}
                        </div>
                    </div>
                    <div className='d-flex flex-row'>
                        {isOwner && (
                            <div id="edit-account">
                                <div>
                                    <button className="btn btn-primary mb-2 me-2" onClick={handleEdit}>Edit Comments</button>
                                </div>
                            </div>
                        )}
                        {isAuthorized && (
                            <div>
                                <button className="btn btn-primary mb-2 me-2" onClick={handleWorktagRequest}>Request Worktag String Change</button>
                            </div>
                        )}
                        {(isOwner && account?.worktagChangeRequest != '') && (
                            <div>
                                <Button variant='warning' className="mb-2 me-2" onClick={handleWorktagRequest}>Review Worktag Change Request</Button>
                            </div>
                        )}
                    </div>
                    <hr className="m-2"></hr>
                </div>

                <div className="col-12 col-md-6">
                    <h2>Account Owner</h2>
                    <p>The Account Owner is the primary point of contact for any related correspondance and may change Postage Account details.</p>
                    <div className="list-group">
                        <div className="list-group-item list-group-item-action" key={account?.owner?.id}>
                            <ul className="list-unstyled my-2">
                                <li><b>Name:</b> {account?.owner?.firstName} {account?.owner?.lastName}</li>
                                <li><b>Department:</b> {account?.owner?.department}</li>
                                <li><b>Email:</b> {account?.owner?.netId}@uw.edu</li>
                            </ul>

                        </div>
                    </div>
                    {isOwner && (
                        <div id="change-owner">
                            <hr />
                            <Button onClick={() => setOwnerTransferOpen(!ownerTransferOpen)} aria-controls="transferOwnershipContainer" aria-expanded={ownerTransferOpen}>
                                <img src={'/img/cycle-icon.svg'} alt="transfer-icon" className='me-2 icon-1-rem'/>Transfer Ownership
                            </Button>
                            <Collapse in={ownerTransferOpen}>
                                <div id="transferOwnershipContainer" className='mt-3'>
                                    <InputGroup>
                                        <input type="text" value={newOwnerNetId} onChange={e => setNewOwnerNetId(e.target.value)} placeholder="Enter new owner NetID" className="form-control" />
                                        <button className="btn btn-success" onClick={handleOwnershipChange}>Confirm Ownership Transfer</button>
                                    </InputGroup>
                                </div>
                            </Collapse>
                            <hr />
                        </div>
                    )}

                </div>

                <div className="col-12 col-md-6">
                    <h2>Authorized Users</h2>
                    <p>Authorized Users may suggest changes to the Worktags. Suggested changes must be approved by owner to take effect.</p>
                    <div className="list-group">
                        {account?.authorizedUsers.map(authUser => (
                            <div className="list-group-item list-group-item-action" key={authUser.id}>
                                <ul className="list-unstyled my-2">
                                    <li><b>Name: </b>{authUser.firstName} {authUser.lastName}</li>
                                    <li><b>Department: </b>{authUser.department}</li>
                                    <li><b>Email: </b>{authUser.netId}@uw.edu</li>
                                </ul>
                            </div>
                        ))}
                    </div>

                    {isOwner && (
                        <div id="add-remove-auth-users">
                            <hr />
                            <Button variant='success' onClick={handleToggleAddUser} aria-expanded={addUserOpen} className='me-3'>
                                Add Authorized User
                            </Button>
                            <Button variant='danger' onClick={handleToggleRemoveUser} aria-expanded={remUserOpen}>
                                Remove Authorized User
                            </Button>
                            <Collapse in={addUserOpen}>
                                <div className='mt-3'>
                                    <InputGroup>
                                        <input type="text" value={newAuthUserNetId} onChange={e => setNewAuthUserNetId(e.target.value)} placeholder="Enter new authorized user NetID" className="form-control" />
                                        <Button onClick={handleAddAuthorizedUser} className='btn btn-success'>Confirm Addition</Button>
                                    </InputGroup>
                                </div>
                            </Collapse>
                            <Collapse in={remUserOpen}>
                                <div className='mt-3'>
                                    <InputGroup>
                                        <input type="text" value={remAuthUserNetId} onChange={e => setRemAuthUserNetId(e.target.value)} placeholder="Enter authorized user NetID to remove" className="form-control" />
                                        <Button onClick={handleRemoveAuthorizedUser} className='btn btn-danger'>Confirm Removal</Button>
                                    </InputGroup>
                                </div>
                            </Collapse>
                            <hr />
                        </div>
                    )}
                    {!isOwner && (
                        <div>
                            <hr />
                            <Button onClick={handleAddAuthorizedUserSelf} className='btn btn-success my-3 me-3'>Join Authorized Users</Button>
                            <Button onClick={handleRemoveAuthorizedUserSelf} className='btn btn-danger my-3'>Leave Authorized Users</Button>
                        </div>
                    )}
                </div>

            </div>
        </div>
    );
}

export default PostageAccountDetails;
